import { useState, useCallback, useRef } from 'react';

interface LoadingOperation {
  id: string;
  operation: string;
  startTime: number;
}

/**
 * Custom hook for managing loading state across multiple asynchronous operations
 * Ensures only a single loading bar is shown regardless of multiple concurrent operations
 * Includes minimum delay before showing to prevent flashing on quick operations
 */
export const useLoadingState = (showDelay: number = 250) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('Loading...');
  const operationsRef = useRef<Map<string, LoadingOperation>>(new Map());
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startLoading = useCallback((operationId: string, message: string = 'Loading...') => {
    operationsRef.current.set(operationId, {
      id: operationId,
      operation: message,
      startTime: Date.now()
    });

    // Update message if this is the first operation or if we want to show a specific message
    if (operationsRef.current.size === 1 || message !== 'Loading...') {
      setCurrentMessage(message);
    }

    // Show loading bar if this is the first operation, with delay
    if (operationsRef.current.size === 1) {
      // Clear any existing timer
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      
      showTimerRef.current = setTimeout(() => {
        setIsLoading(true);
        showTimerRef.current = null;
      }, showDelay);
    }
  }, [showDelay]);

  const stopLoading = useCallback((operationId: string) => {
    const operation = operationsRef.current.get(operationId);
    if (operation) {
      operationsRef.current.delete(operationId);

      // If this was the last operation, hide the loading bar and clear timer
      if (operationsRef.current.size === 0) {
        // Clear any pending show timer
        if (showTimerRef.current) {
          clearTimeout(showTimerRef.current);
          showTimerRef.current = null;
        }
        
        setIsLoading(false);
        setCurrentMessage('Loading...');
      } else {
        // Update message to show the next operation
        const nextOperation = Array.from(operationsRef.current.values())[0];
        setCurrentMessage(nextOperation.operation);
      }
    }
  }, []);

  const clearAllLoading = useCallback(() => {
    operationsRef.current.clear();
    
    // Clear any pending show timer
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    
    setIsLoading(false);
    setCurrentMessage('Loading...');
  }, []);

  const getActiveOperations = useCallback(() => {
    return Array.from(operationsRef.current.values());
  }, []);

  return {
    isLoading,
    message: currentMessage,
    startLoading,
    stopLoading,
    clearAllLoading,
    getActiveOperations
  };
};

/**
 * Higher-order function to wrap async operations with loading state
 */
export const withLoading = <T extends any[], R>(
  operation: (...args: T) => Promise<R>,
  operationId: string,
  message: string = 'Loading...'
) => {
  return async (
    startLoading: (id: string, msg: string) => void,
    stopLoading: (id: string) => void,
    ...args: T
  ): Promise<R> => {
    startLoading(operationId, message);
    try {
      const result = await operation(...args);
      return result;
    } finally {
      stopLoading(operationId);
    }
  };
};
